import { Button, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { clearSelectionsAtom, selectedOpsIdsAtom, selectedStepsIdsAtom } from '~/atoms/no-code-atoms';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { GetTestContentType } from '~/server/api/routers/test-content';
import { api } from '~/trpc-extra/api';

/**
 * Component for converting selected operations to AI actions
 * Handles validation, confirmation modal, and API call
 */
export function ConvertToAIButton({ readonly, testContent }: { readonly?: boolean; testContent?: GetTestContentType }) {
  const selectedStepsIds = useAtomValue(selectedStepsIdsAtom);
  const selectedOpsIds = useAtomValue(selectedOpsIdsAtom);
  const clearSelections = useSetAtom(clearSelectionsAtom);
  const { recorderCursor, updateLiveState } = useLiveStateManager();

  const trpcUtils = api.useUtils();

  // Get selected operations from the test content
  const selectedOps = useMemo(() => {
    const selectedOpsIdsSet = new Set(selectedOpsIds);
    return testContent?.steps.flatMap((step) => step.operations).filter((op) => selectedOpsIdsSet.has(op.id)) ?? [];
  }, [selectedOpsIds, testContent?.steps]);

  // Check if selected operations are adjacent within their respective steps
  const areOperationsAdjacent = useMemo(() => {
    if (selectedOps.length <= 1) {
      return false;
    }

    const opsByStep = Object.groupBy(selectedOps, (op) => op.testStep?.id || '');
    const stepIds = Object.keys(opsByStep);

    // Only allow adjacency check if all operations are within the same step
    if (stepIds.length > 1) {
      return false;
    }

    // Check if operations within the single step are adjacent
    const stepId = stepIds[0];
    const stepOps = opsByStep[stepId]?.sort((a, b) => a.order - b.order) || [];

    if (stepOps.length <= 1) {
      return false;
    }

    const allStepOps = testContent?.steps.find((step) => step.id === stepId)?.operations || [];

    const firstSelectedOpIndex = allStepOps.findIndex((op) => op.id === stepOps[0].id);
    const lastSelectedOpIndex = allStepOps.findIndex((op) => op.id === stepOps.at(-1)?.id);

    const indexGap = lastSelectedOpIndex - firstSelectedOpIndex;
    return indexGap === stepOps.length - 1;
  }, [selectedOps, testContent?.steps]);

  // Check if we can convert selected operations to AI
  const canConvertToAI = useMemo(() => {
    // Only allow if we have selected operations (no steps) and they are adjacent within the same step
    const hasOnlyOps = selectedStepsIds.length === 0 && selectedOps.length > 0;
    const hasAdjacentOps = selectedOps.length > 1 && areOperationsAdjacent;
    const hasSingleOp = selectedOps.length === 1;

    return hasOnlyOps && (hasAdjacentOps || hasSingleOp);
  }, [selectedStepsIds.length, selectedOps.length, areOperationsAdjacent]);

  const { mutate: convertOperationsToAI, isPending: isConvertingToAI } = api.operation.convertToAI.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Converting operations to AI...', color: 'blue' });
    },
    onSuccess: (lastCreatedOperation) => {
      // if the recorder state contains any of the operations that we just converted, we need to update the recorder state
      if (selectedOps.some((op) => recorderCursor?.operation?.id === op.id)) {
        updateLiveState((prev) => ({
          ...prev,
          recorderCursor: {
            stepId: selectedOps[0].testStep?.id || '',
            stepOrder: selectedOps[0].order,
            operation:
              lastCreatedOperation ? { id: lastCreatedOperation.id, order: lastCreatedOperation.order } : undefined,
            parentContext: undefined,
          },
        }));
      }
      notifications.show({ message: 'Actions converted to AI successfully', color: 'teal' });
      trpcUtils.testContent.get.invalidate({ id: testContent?.id }).catch(() => undefined);
      clearSelections();
    },
    onError: () => {
      notifications.show({ message: `Failed to convert to AI actions`, color: 'red' });
    },
  });

  const handleConvertToAIClick = () => {
    if (!canConvertToAI || selectedOps.length === 0) {
      return;
    }

    modals.openConfirmModal({
      title: 'Convert to AI Actions',
      children: (
        <Text>
          This will replace the selected {selectedOps.length} operation{selectedOps.length > 1 ? 's' : ''} with AI
          action{selectedOps.length > 1 ? 's' : ''} that can perform the same tasks intelligently.
        </Text>
      ),
      labels: { confirm: 'Convert to AI', cancel: 'Cancel' },
      centered: true,
      onConfirm: () => {
        convertOperationsToAI({
          operationIds: selectedOps.map((op) => op.id),
        });
      },
    });
  };

  // Don't render if no steps are selected (only show for operations)
  if (selectedStepsIds.length > 0) {
    return null;
  }

  return (
    <Tooltip
      label={
        !canConvertToAI ?
          'Select adjacent actions within the same step to convert to AI'
        : `Convert ${selectedOps.length} selected action${selectedOps.length > 1 ? 's' : ''} to AI action${selectedOps.length > 1 ? 's' : ''}`
      }
    >
      <Button
        variant="light"
        onClick={handleConvertToAIClick}
        size="compact-sm"
        disabled={!canConvertToAI || readonly || isConvertingToAI}
        loading={isConvertingToAI}
      >
        AI Action
      </Button>
    </Tooltip>
  );
}
